import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

export default function Sidebar({ brand, sections, footer, defaultCollapsed = false }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <aside
      className={`
        ${collapsed ? 'w-[68px]' : 'w-[240px]'}
        shrink-0 bg-[var(--color-cream-50)]
        border-r border-[var(--color-warm-100)]
        flex flex-col
        transition-all duration-200 ease-out
        sticky top-0 h-screen
      `}
    >
      {/* Brand */}
      <div className={`h-16 flex items-center ${collapsed ? 'justify-center' : 'px-5'} border-b border-[var(--color-warm-100)] shrink-0`}>
        {brand && (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-warm-900)] text-[var(--color-cream-50)] flex items-center justify-center font-extrabold text-[13px] shrink-0">
              {brand.initials || 'R'}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-[13px] font-bold text-[var(--color-warm-900)] leading-tight truncate">
                  {brand.name || 'Reconciliation Hub'}
                </div>
                {brand.tagline && (
                  <div className="text-[11px] text-[var(--color-warm-500)] truncate">{brand.tagline}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        {sections.map((section, i) => (
          <div key={i} className="mb-6">
            {!collapsed && section.label && (
              <div className="px-5 mb-2 text-[10px] font-bold uppercase tracking-[1.2px] text-[var(--color-warm-400)]">
                {section.label}
              </div>
            )}
            <ul className="space-y-0.5 px-2">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) => `
                      flex items-center gap-3 h-9
                      ${collapsed ? 'justify-center w-full' : 'px-3'}
                      rounded-[var(--radius-sm)]
                      text-[13px] font-medium
                      transition-colors
                      ${
                        isActive
                          ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)]'
                          : 'text-[var(--color-warm-700)] hover:bg-[var(--color-cream-200)] hover:text-[var(--color-warm-900)]'
                      }
                    `}
                  >
                    <span className="shrink-0 [&_svg]:w-[18px] [&_svg]:h-[18px]">
                      {item.icon}
                    </span>
                    {!collapsed && (
                      <>
                        <span className="truncate flex-1">{item.label}</span>
                        {item.badge != null && item.badge > 0 && (
                          <span className="shrink-0 text-[10px] font-bold px-1.5 h-5 min-w-[20px] flex items-center justify-center rounded-full bg-[var(--color-action-500)] text-white tabular-nums">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer */}
      {footer && (
        <div className="p-3 border-t border-[var(--color-warm-100)] shrink-0">
          {footer}
        </div>
      )}

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="h-10 border-t border-[var(--color-warm-100)] text-[var(--color-warm-500)] hover:bg-[var(--color-cream-200)] hover:text-[var(--color-warm-900)] flex items-center justify-center transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
      </button>
    </aside>
  );
}
